import { NavLink as RouterLink } from "react-router-dom";
import Tour from "reactour";
// material
import {
  Box,
  Stack,
  Typography,
  Button,
  Container,
  styled,
  Switch,
} from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import Image from "src/components/Image";
import { PATH_DASHBOARD } from "src/routes/paths";
import { useState } from "react";
import { CheckOnboardingDone } from "src/utils/apis";
import useAuth from "src/hooks/useAuth";

// ----------------------------------------------------------------------
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "rgba(130, 211, 97, 1)",
  },
}));
const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 32,
  height: 16,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#65C466",
        opacity: 1,
        border: 0,
        ...theme.applyStyles("dark", {
          backgroundColor: "#2ECA45",
        }),
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
      ...theme.applyStyles("dark", {
        color: theme.palette.grey[600],
      }),
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
      ...theme.applyStyles("dark", {
        opacity: 0.3,
      }),
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 12,
    height: 12,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "rgba(132, 132, 132, 1)",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    ...theme.applyStyles("dark", {
      backgroundColor: "#39393D",
    }),
  },
}));
const TOUR_STEP_STYLE = {
  color: "rgba(74, 74, 74, 0.8)",
  fontSize: 14,
  fontWeight: 400,
  padding: "2rem",
};

const DEFAULT_STEPS = [
  {
    selector: ".menu-dashboard",
    content:
      "See a comprehensive view of all your bookings, earnings, listings, and guest activity all in one place to help you manage your property with ease.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-all-listings",
    content:
      "View all your properties, add new listings, edit details, and track the status of each one — all from one place.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-messages, .menu-messaging",
    content:
      "Inbuilt messaging with real-time notifications, advanced filtering, and bulk messaging capabilities.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-earnings",
    content:
      "Earnings overview based on listings, with a breakdown for Afritas and host bookings, flexible reporting, and historical comparison.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-insights",
    content:
      "Detailed view breakdown by location, domestic/international, device type, and custom filters.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-wallet",
    content:
      "AfriPay Wallet — view your wallet balance, transfer money, pay bills, and manage your earnings seamlessly from one place.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-activity-logs",
    content:
      "Complete activity logs with detailed tracking, including edits, log-ins, and staff changes.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
];

const EVENT_STEPS = [
  {
    selector: ".menu-dashboard",
    content:
      "See a comprehensive view of your ticket sales, overall earnings, event listings, and guest activity all in one place to help you manage your ticketing with ease.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-guest-list",
    content:
      "View and manage your event guest list, check ticket status, and monitor attendee details in real time.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-ticket-types",
    content:
      "Configure ticket categories (VIP, Early Bird, Regular), set prices, control quantities, and update ticket configurations.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-checked-in",
    content:
      "Track live attendee check-ins, scan ticket barcodes and QR codes, and view real-time entry statistics.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-add-ticket",
    content:
      "Quickly create and publish new ticket tiers with customized pricing and capacity limits for your events.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-all-listings",
    content:
      "View all your event listings, edit event descriptions, manage dates, and track published status in one place.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-add-listing",
    content:
      "Create a new event listing step-by-step with photos, venue locations, schedule, and ticket structures.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-reports",
    content:
      "Access detailed reports on ticket sales revenue, attendance trends, payout statuses, and event performance analytics.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-staff",
    content:
      "Invite your event team members and assign specific staff roles to handle ticket scanning, check-ins, and guest management.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-transactions",
    content:
      "Track all ticket purchase transactions, payment methods, platform fees, and net payouts in real time.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-earnings",
    content:
      "Review your total earnings overview across event listings, flexible revenue reporting, and historical performance.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-wallet",
    content:
      "View your AfriPay Wallet balance, transfer funds, receive payouts, and manage ticket earnings seamlessly.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-messaging",
    content:
      "Communicate directly with ticket holders and attendees with real-time notifications and message history.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-insights",
    content:
      "Gain deep insights into attendee demographics, ticket purchase channels, device usage, and location analytics.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-activity-logs",
    content:
      "Complete activity log with detailed tracking of staff logins, ticket updates, and administrative actions.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
];

const ATTRACTION_STEPS = [
  {
    selector: ".menu-dashboard",
    content:
      "See a comprehensive view of your attraction bookings, visitor counts, revenue, and active listings all in one place.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-guest-list",
    content:
      "View visitor reservations, manage entry passes, and inspect visitor booking details in real time.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-ticket-types",
    content:
      "Manage entry pass categories, adult and child pricing tiers, group discounts, and visit schedules.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-checked-in",
    content:
      "Track visitor entry check-ins live, scan entry barcodes, and monitor daily visitor statistics.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-add-ticket",
    content:
      "Create new entry pass options, special admission tickets, and seasonal visitor passes.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-all-listings",
    content:
      "View all your attraction listings, update opening hours, edit details, and track published status.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-add-listing",
    content:
      "Add a new attraction listing step-by-step with photos, location maps, opening hours, and pricing passes.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-reports",
    content:
      "View comprehensive reports on visitor numbers, peak admission hours, revenue breakdowns, and performance trends.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-staff",
    content:
      "Invite staff members and assign specific roles to manage ticket validation, entry gates, and visitor support.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-transactions",
    content:
      "Track visitor entry pass transactions, payment methods, and net payout earnings.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-earnings",
    content:
      "Overview of attraction earnings, payout history, platform fee breakdowns, and historical comparisons.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-wallet",
    content:
      "Manage your AfriPay Wallet balance, process payouts, pay bills, and manage attraction revenues effortlessly.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-messaging",
    content:
      "Communicate with visitors, answer visitor inquiries, and send instant booking notifications.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-insights",
    content:
      "Analyze visitor demographics, peak visitation dates, booking sources, and location analytics.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-activity-logs",
    content:
      "Track detailed activity logs including staff logins, pass updates, and operational changes.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
];

const VEHICLE_STEPS = [
  {
    selector: ".menu-dashboard",
    content:
      "See a comprehensive view of all your vehicle rentals, earnings, listings, and driver activity all in one place.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-all-listings",
    content:
      "View all your vehicle listings, add new cars, edit specifications, and track rental status.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-messaging",
    content:
      "Communicate with customers renting your vehicles with real-time messaging.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-earnings",
    content:
      "Vehicle rental earnings overview, fee breakdowns, and payout reports.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
  {
    selector: ".menu-wallet",
    content:
      "AfriPay Wallet — manage your vehicle rental income, request payouts, and transfer funds.",
    position: "bottom",
    style: TOUR_STEP_STYLE,
  },
];

export default function OnboardingBanner({ setOpen }) {
  const { user } = useAuth();
  const businessType = user && user.user ? parseInt(user.user.type, 10) : 0;
  const onboarding = CheckOnboardingDone();
  const [isTour, setIsTour] = useState(false);

  const getTourSteps = () => {
    if (businessType === 1) return EVENT_STEPS;
    if (businessType === 6) return ATTRACTION_STEPS;
    if (businessType === 2) return VEHICLE_STEPS;
    return DEFAULT_STEPS;
  };

  return (
    <Box sx={{ pt: { lg: 5, sm: 12, xs: 14 }, width: 1 }}>
      <Container maxWidth="xl">
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Image
                src="/icons/laptop-mobile.svg"
                alt="setup icon"
                width={16}
                height={16}
              />
              <Typography
                sx={{ color: "text.secondary", fontWeight: 400, fontSize: 14 }}
                variant="body2"
              >
                Setup
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IOSSwitch
                size="small"
                checked={isTour}
                onChange={(e) => {
                  setOpen(true);
                  setTimeout(() => {
                    setIsTour(true);
                  }, 500);
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
              <Typography
                sx={{ color: "text.secondary", fontWeight: 400, fontSize: 14 }}
                variant="body2"
              >
                Tour
              </Typography>
            </Stack>
          </Stack>
          <Box
            sx={{
              boxShadow: "-1px 1px 12px 0px rgba(0, 0, 0, 0.09)",
              bgcolor: "background.default",
              borderRadius: 1,
              p: 4,
            }}
          >
            <Stack
              direction={{ md: "row", sm: "column" }}
              alignItems="center"
              justifyContent="space-between"
              spacing={{ md: 15, xs: 2 }}
            >
              <Stack spacing={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "rgba(74, 74, 74, 1)" }}
                >
                  Let Us Help You Get Started
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 400,
                    fontSize: 13,
                    color: "rgba(74, 74, 74, 0.8)",
                  }}
                >
                  Set up your account and explore the Afritas platform with
                  ease. We’ll guide you through everything you need to know to
                  succeed as a vendor.
                </Typography>
              </Stack>
              <Stack
                spacing={{ md: 4, xs: 2 }}
                sx={{
                  width: 1,
                  maxWidth: { md: 300, xs: 1 },
                  flexShrink: 0,
                  flexGrow: 1,
                }}
                justifyContent={{ md: "end", xs: "center" }}
                alignItems={{ md: "end", xs: "center" }}
                direction={{
                  md: "column",
                  sm: "row-reverse",
                  xs: "row-reverse",
                }}
              >
                <Box sx={{ maxWidth: 150, width: 1 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    component={RouterLink}
                    to={PATH_DASHBOARD.onboarding}
                    sx={{ boxShadow: "none", fontSize: 14, fontWeight: 500 }}
                    startIcon={
                      <Image
                        isCenter
                        src="/icons/user-salary.svg"
                        alt="setup icon"
                        sx={{
                          "& img": {
                            filter: "brightness(0) invert(1)",
                          },
                        }}
                        width={16}
                      />
                    }
                  >
                    Continue
                  </Button>
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  justifyContent="end"
                  sx={{ width: 1 }}
                >
                  <BorderLinearProgress
                    variant="determinate"
                    value={parseFloat(onboarding.progress || 0)}
                    sx={{ width: 1 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {onboarding.progress}%
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Container>
      {isTour && (
        <Tour
          steps={getTourSteps()}
          isOpen={isTour}
          rounded={5}
          accentColor={"rgba(65, 177, 26, 1)"}
          onRequestClose={() => setIsTour(false)}
        />
      )}
    </Box>
  );
}
